import { Container, Grid, Card, CardHeader, CardContent, Divider, Typography, FormControl, InputLabel, Select, MenuItem, SelectChangeEvent, Tooltip } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useUserContext } from 'src/components/ProtectedRoute/userProvider';
import Footer from 'src/components/Footer';
import { useImageCount } from '../Hooks/useImageCount';
import logger from 'src/logger';
import { useMapVisibility } from '../Hooks/useMapVisibility';
import ImageCard from '../Add/ImageCard';
import { MapComponentContainer } from '../Components/MapComponentContainer';
import { FormControlLabel, Checkbox, Box, Alert, Chip, Link } from '@mui/material';
import { useFileUpload } from './useFileUpload';
import { useEffect, useState } from 'react';
import { getUser, hasUserEnteredCompetition, enterCompetition, getCompetitions } from 'src/apiCalls';
import { Button, Modal, IconButton } from '@mui/material';
import MapIcon from '@mui/icons-material/Map';
import CloseIcon from '@mui/icons-material/Close';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import { FileInfo } from '../Types';

// --- Constants ---
const CURRENT_MONTH = new Date().toLocaleString('default', { month: 'long' });
const CURRENT_YEAR = new Date().getFullYear();

function AddImage() {
    const navigate = useNavigate();
    const { user } = useUserContext();
    const { imageCount, uploadDisabled } = useImageCount(user);
    const [agreedToTerms, setAgreedToTerms] = useState(false);
    const [mapModalOpen, setMapModalOpen] = useState(false);
    const [shouldEnterCompetition, setShouldEnterCompetition] = useState(false);
    const [hasEntered, setHasEntered] = useState<boolean>(false);
    const [competitionError, setCompetitionError] = useState<string>('');
    const [competitions, setCompetitions] = useState<any[]>([]);
    const [selectedCompetition, setSelectedCompetition] = useState<string | null>(null);

    const handleOpenMap = () => setMapModalOpen(true);
    const handleCloseMap = () => setMapModalOpen(false);
    const {
        fileInfo,
        errorMessage,
        isProcessing,
        handleFileUpload,
        updateField,
        uploadFile,
        clearFile,
        isSubmitDisabled,
        hasValidationErrors
    } = useFileUpload(user);
    const [stripeOnboardingComplete, setStripeOnboardingComplete] = useState(false);

    useEffect(() => {
        const fetchStripeOnboardingStatus = async () => {
            try {
                const userData = await getUser(user.accessToken, user.uid);
                if (userData) {
                    setStripeOnboardingComplete(userData.stripeOnboardingComplete);
                }

            } catch (error) {
                logger.error('Error fetching Stripe onboarding status:', error);
            }
        };

        fetchStripeOnboardingStatus();
    }, [user]);

    useEffect(() => {
        const fetchCompetitions = async () => {
            try {
                const competitionsList = await getCompetitions();
                setCompetitions(competitionsList);
                if (competitionsList.length > 0) {
                    setSelectedCompetition(competitionsList[0].id);
                }
            } catch (error) {
                logger.error('Error fetching competitions:', error);
            }
        };

        fetchCompetitions();
    }, []);

    useEffect(() => {
        const checkCompetitionEntry = async () => {
            if (!user?.uid || !selectedCompetition) return;

            try {
                const alreadyEntered = await hasUserEnteredCompetition(user.uid, selectedCompetition);
                setHasEntered(alreadyEntered);

                // If user has already entered, disable the competition checkbox
                if (alreadyEntered) {
                    setShouldEnterCompetition(false);
                    setCompetitionError('You have already entered this competition.');
                } else {
                    setCompetitionError('');
                }
            } catch (error) {
                logger.error('Error checking competition entry status:', error);
                setCompetitionError('Error checking competition entry status. Please try again.');
            }
        };

        checkCompetitionEntry();
    }, [user, selectedCompetition]);

    const {
        showMap,
        toggleMapVisibility
    } = useMapVisibility();

    const handleFileInput = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0] || null;
        handleFileUpload(file);
    };

    const handleSubmit = async () => {
        try {
            const success = await uploadFile();
            if (success) {
                // If the user wants to enter the competition and we're in the entry phase
                if (shouldEnterCompetition && selectedCompetition && !hasEntered) {
                    try {
                        await handleEnterCompetition();
                        logger.info('Image entered into competition successfully');
                    } catch (error) {
                        logger.error('Error entering image into competition:', error);
                        // Continue with navigation even if competition entry fails
                    }
                }

                const uniqueKey = new Date().getTime();
                navigate(`/images/view?refresh=${uniqueKey}`);
            }
        } catch (error) {
            logger.error('Error during file upload:', error);
        }
    };

    const handleEnterCompetition = async () => {
        if (!fileInfo) {
            logger.error('No file info available');
            return;
        }
        try {
            await enterCompetition(user.accessToken, user.uid, fileInfo.id, selectedCompetition);
            logger.info('Image entered into competition successfully');
        } catch (error) {
            logger.error('Error entering image into competition:', error);
            // Continue with navigation even if competition entry fails
        }
    };

    const handleLocationChange = (field: keyof FileInfo, value: any) => {
        updateField(field, value);
    };

    const handlePriceChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value === '' ? undefined : parseFloat(event.target.value);
        updateField('price', value);
    };

    const handleSpeciesChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        updateField('species', event.target.value);
    };

    const handleWeightChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        logger.debug('AddImage - handleWeightChange called with value:', event.target.value);
        const value = event.target.value === '' ? undefined : parseFloat(event.target.value);
        logger.debug('AddImage - parsed weight value:', value);
        updateField('weight', value);
    };

    const handleAreaChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        updateField('area', event.target.value);
    };

    const handleTagsChange = (newTags: string) => {
        updateField('tags', newTags);
    };

    const handleCompetitionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (hasEntered) {
            setShouldEnterCompetition(false);
            return;
        }

        setShouldEnterCompetition(event.target.checked);
    };

    const handleCompetitionSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSelectedCompetition(event.target.value);
        setShouldEnterCompetition(false);
    };

    return (
        <>
            <Container maxWidth="lg" sx={{ mt: 4 }}>
                <Grid container spacing={3} justifyContent="center">
                    <Grid item xs={12}>
                        <Card>
                            <CardHeader title="Add Catch Report" />
                            <Divider />
                            <CardContent>
                                {imageCount >= 11 ? (
                                    <Typography color="error" variant="body1">
                                        The site currently has a limit of 10 catch reports per user.
                                        Please delete some catch reports to upload more.
                                    </Typography>
                                ) : (
                                    !fileInfo && (
                                        <Box 
                                            sx={{ 
                                                display: 'flex', 
                                                flexDirection: 'column', 
                                                alignItems: 'center',
                                                gap: 2,
                                                py: 4
                                            }}
                                        >
                                            <input
                                                type="file"
                                                onChange={handleFileInput}
                                                disabled={uploadDisabled || isProcessing}
                                                style={{ display: 'none' }}
                                                id="catch-report-input"
                                            />
                                            <label htmlFor="catch-report-input">
                                                <Button
                                                    variant="contained"
                                                    component="span"
                                                    disabled={uploadDisabled || isProcessing}
                                                    sx={{
                                                        px: 4,
                                                        py: 1.5,
                                                        fontSize: '1.1rem'
                                                    }}
                                                >
                                                    {isProcessing ? 'Processing...' : 'Add Catch Report'}
                                                </Button>
                                            </label>
                                            <Typography variant="body2" color="text.secondary">
                                                Select a JPG/JPEG image to upload
                                            </Typography>
                                        </Box>
                                    )
                                )}

                                {fileInfo && (
                                    <>
                                        <Box sx={{ mb: 4 }}>
                                            <ImageCard
                                                data={fileInfo}
                                                handlePriceChange={(value) => updateField('price', value === '' ? undefined : parseFloat(value))}
                                                handleTagsChange={(value) => updateField('tags', value)}
                                                handleSpeciesChange={(_, value) => updateField('species', value || '')}
                                                handleWeightChange={(value) => {
                                                    logger.debug('AddImage - ImageCard weight change received:', value);
                                                    updateField('weight', value === '' ? undefined : value);
                                                }}
                                                handleLocationChange={(field, value) => updateField(field, value)}
                                                handleAreaChange={(event) => updateField('area', event.target.value)}
                                                handlePublicizeLocationChange={(value) => updateField('publicizeLocation', value)}
                                                handleChargeLocationChange={(value) => updateField('chargeForLocation', value)}
                                                handleFreeForRegisteredUsersChange={(value) => updateField('freeForRegisteredUsers', value)}
                                                handleFreeForEveryoneChange={(value) => updateField('freeForEveryone', value)}
                                                handleMinimumPhotoUploadsChange={(value) => updateField('minimumPhotoUploads', value)}
                                                isSubmitted={isProcessing}
                                                stripeOnboardingComplete={stripeOnboardingComplete}
                                                handleOpenMap={handleOpenMap}
                                            />
                                        </Box>

                                        {/* Competition Entry Section */}
                                        {competitions.length > 0 && (
                                            <Box sx={{ mb: 3, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                                    <CalendarMonthIcon color="primary" sx={{ mr: 1 }} />
                                                    <Typography variant="h6" component="h2">
                                                        Competition Entry
                                                    </Typography>
                                                </Box>

                                                <FormControl fullWidth sx={{ mb: 2 }}>
                                                    <InputLabel id="competition-select-label">Select Competition</InputLabel>
                                                    <Select
                                                        labelId="competition-select-label"
                                                        value={selectedCompetition}
                                                        label="Select Competition"
                                                        onChange={handleCompetitionSelect}
                                                    >
                                                        {competitions.map((competition) => (
                                                            <MenuItem key={competition.id} value={competition.id}>
                                                                {competition.title}
                                                            </MenuItem>
                                                        ))}
                                                    </Select>
                                                </FormControl>

                                                {hasEntered ? (
                                                    <Alert severity="info" sx={{ mb: 2 }}>
                                                        You have already entered this competition.
                                                    </Alert>
                                                ) : (
                                                    <FormControlLabel
                                                        control={
                                                            <Checkbox
                                                                checked={shouldEnterCompetition}
                                                                onChange={handleCompetitionChange}
                                                                disabled={hasEntered}
                                                            />
                                                        }
                                                        label="Enter this catch in the competition"
                                                    />
                                                )}

                                                {/* {competitionError && (
                                                    <Alert severity="error" sx={{ mt: 1 }}>
                                                        {competitionError}
                                                    </Alert>
                                                )} */}
                                            </Box>
                                        )}

                                        <Box sx={{ 
                                            mt: 3,
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: 2
                                        }}>
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        checked={agreedToTerms}
                                                        onChange={() => setAgreedToTerms(!agreedToTerms)}
                                                        color="primary"
                                                    />
                                                }
                                                label={
                                                    <Typography variant="body2">
                                                        I agree to the{' '}
                                                        <Link 
                                                            href="/terms" 
                                                            target="_blank" 
                                                            rel="noopener noreferrer"
                                                            color="primary"
                                                            underline="hover"
                                                        >
                                                            terms and conditions
                                                        </Link>
                                                    </Typography>
                                                }
                                            />

                                            <Box sx={{ 
                                                display: 'flex', 
                                                gap: 2, 
                                                justifyContent: 'flex-end',
                                                borderTop: 1,
                                                borderColor: 'divider',
                                                pt: 2
                                            }}>
                                                <Button
                                                    variant="outlined"
                                                    onClick={clearFile}
                                                    disabled={isProcessing}
                                                    color="secondary"
                                                >
                                                    Cancel
                                                </Button>
                                                <Tooltip title={
                                                    !agreedToTerms 
                                                        ? "Please agree to the terms and conditions"
                                                        : hasValidationErrors 
                                                            ? "Please fill in all required fields"
                                                            : isProcessing
                                                                ? "Processing..."
                                                                : ""
                                                } arrow>
                                                    <span>  {/* Wrapper needed for disabled button tooltip */}
                                                        <Button
                                                            variant="contained"
                                                            onClick={handleSubmit}
                                                            disabled={isSubmitDisabled || !agreedToTerms}
                                                            color="primary"
                                                        >
                                                            {isProcessing ? 'Uploading...' : 'Upload'}
                                                        </Button>
                                                    </span>
                                                </Tooltip>
                                            </Box>
                                        </Box>
                                    </>
                                )}

                                {errorMessage && (
                                    <Typography color="error" sx={{ mt: 2 }}>
                                        {errorMessage}
                                    </Typography>
                                )}
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>

                {/* Map Modal */}
                <Modal
                    open={mapModalOpen}
                    onClose={handleCloseMap}
                    aria-labelledby="map-modal"
                >
                    <Box
                        sx={{
                            position: 'fixed',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            width: '50vw',
                            height: '37.5vw',
                            maxWidth: '600px',
                            maxHeight: '450px',
                            bgcolor: 'background.paper',
                            boxShadow: 24,
                            borderRadius: 1,
                            outline: 'none',
                            overflow: 'hidden'
                        }}
                    >
                        <IconButton
                            onClick={handleCloseMap}
                            sx={{
                                position: 'absolute',
                                right: 8,
                                top: 8,
                                zIndex: 1,
                                bgcolor: 'rgba(255, 255, 255, 0.7)',
                                '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.9)' }
                            }}
                        >
                            <CloseIcon />
                        </IconButton>
                        {fileInfo && (
                            <MapComponentContainer
                                latitude={fileInfo.latitude}
                                longitude={fileInfo.longitude}
                                useWrapper={false}
                                height="100%"
                            />
                        )}
                    </Box>
                </Modal>

                <Footer />
            </Container>
        </>
    );
}

export default AddImage;